import {
  createContext,
  type Dispatch,
  type JSX,
  type ReactNode,
  type SetStateAction,
  useContext,
  useMemo,
} from 'react';
import { type TernaryDarkMode } from 'usehooks-ts';

interface ApplicationContextProviderProps {
  isDarkMode: boolean;
  ternaryDarkMode: TernaryDarkMode;
  setTernaryDarkMode: Dispatch<SetStateAction<TernaryDarkMode>>;
  children: ReactNode;
}

interface ApplicationContextType {
  isDarkMode: boolean;
  ternaryDarkMode: TernaryDarkMode;
  setTernaryDarkMode: Dispatch<SetStateAction<TernaryDarkMode>>;
}

const ApplicationContext = createContext<ApplicationContextType | undefined>(undefined);

function ApplicationContextProvider({
  isDarkMode,
  ternaryDarkMode,
  setTernaryDarkMode,
  children,
}: Readonly<ApplicationContextProviderProps>): JSX.Element {
  const context = useMemo(
    () => ({ isDarkMode, ternaryDarkMode, setTernaryDarkMode }),
    [isDarkMode, setTernaryDarkMode, ternaryDarkMode],
  );

  return (
    <ApplicationContext.Provider value={context}>
      {children}
    </ApplicationContext.Provider>
  );
}

const errorMessage = 'useDarkModeContext has to be used within <ApplicationContextProvider>';

export const useDarkModeContext = () => {
  const currentContext = useContext(ApplicationContext);
  if (!currentContext) {
    throw new Error(errorMessage);
  }
  return currentContext;
};

export default ApplicationContextProvider;
